import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import type { AdapterCapabilities } from '@/api';
import { useRustyState } from '@/state';

const SIGNED = {
  signed: true,
  retryable: true,
  replayable: true,
  signature_header: 'X-Formshive-Signature',
  idempotency_header: 'Idempotency-Key',
};
const UNSIGNED = {
  signed: false,
  retryable: true,
  replayable: true,
};

/** Mirrors the backend adapters and `integrations/signing.rs`; used until `/integrations/capabilities` answers. */
export const FALLBACK_CAPABILITIES: Record<string, AdapterCapabilities> = {
  generic_webhook: { kind: 'generic_webhook', ...SIGNED },
  zapier_webhook: { kind: 'zapier_webhook', ...SIGNED },
  formshive_endpoint: { kind: 'formshive_endpoint', ...SIGNED },
  slack_url: { kind: 'slack_url', ...UNSIGNED },
  discord_url: { kind: 'discord_url', ...UNSIGNED },
  google_sheets_webhook: { kind: 'google_sheets_webhook', ...UNSIGNED },
  api_pipedrive: { kind: 'api_pipedrive', ...UNSIGNED },
  api_mailchimp: { kind: 'api_mailchimp', ...UNSIGNED },
  api_kit: { kind: 'api_kit', ...UNSIGNED },
  email: { kind: 'email', signed: false, retryable: false, replayable: false },
};

export type CapabilitiesMap = Record<string, AdapterCapabilities>;

let pending: Promise<CapabilitiesMap> | null = null;

function loadCapabilities(): Promise<CapabilitiesMap> {
  if (!pending) {
    pending = useRustyState
      .getState()
      .api.getIntegrationCapabilities({ onError: false })
      .then((rows) => {
        const merged: CapabilitiesMap = { ...FALLBACK_CAPABILITIES };
        for (const c of rows) merged[c.kind] = c;
        return merged;
      })
      .catch(() => FALLBACK_CAPABILITIES);
  }
  return pending;
}

/** Capabilities per adapter kind: the static table at once, the endpoint's answer when it arrives. */
export function useIntegrationCapabilities(): CapabilitiesMap {
  const [caps, setCaps] = useState<CapabilitiesMap>(FALLBACK_CAPABILITIES);
  useEffect(() => {
    let live = true;
    loadCapabilities().then((c) => {
      if (live) setCaps(c);
    });
    return () => {
      live = false;
    };
  }, []);
  return caps;
}

export function capabilitiesFor(caps: CapabilitiesMap, kind: string): AdapterCapabilities {
  return caps[kind] ?? { kind, ...UNSIGNED };
}

/** The "If a delivery fails" sentence. */
export function failureText(c: AdapterCapabilities, t: TFunction): string {
  if (c.kind === 'email') return t('integrations.failure.email');
  if (c.retryable && c.replayable) {
    return c.kind === 'google_sheets_webhook'
      ? t('integrations.failure.retrySlowResend')
      : t('integrations.failure.retryResend');
  }
  if (c.retryable) return t('integrations.failure.retryOnly');
  if (c.replayable) return t('integrations.failure.resendOnly');
  return t('integrations.failure.none');
}

/** "Signed with a secret ... / Not signed." followed by the failure sentence. */
export function deliveryFacts(c: AdapterCapabilities, t: TFunction): string {
  const signing = c.signed ? t('integrations.facts.signed') : t('integrations.facts.notSigned');
  return `${signing} ${failureText(c, t)}`;
}
